import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Trash2, GripVertical, Settings, FolderOpen } from 'lucide-react';
import { eventsApi, screensApi } from '../lib/api';
import { toast } from 'sonner';

// Normalise any stored date string to the YYYY-MM-DDTHH:mm format required by datetime-local inputs
const toDateTimeLocal = (val) => {
  if (!val) return '';
  // Replace space separator with T and trim to minute precision
  return val.replace(' ', 'T').substring(0, 16);
};

export const EventSettingsDialog = ({ open, onOpenChange, event, screens, onEventUpdate, onScreensChange }) => {
  const [eventForm, setEventForm] = useState({ name: '', description: '', scheduled_date: '', streaming_url: '' });
  const [savingEvent, setSavingEvent] = useState(false);

  const [newResourceName, setNewResourceName] = useState('');
  const [addingResource, setAddingResource] = useState(false);

  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDrop = async (targetId) => {
    if (!draggingId || draggingId === targetId) { setDraggingId(null); setDragOverId(null); return; }
    const oldIdx = screens.findIndex(s => s.id === draggingId);
    const newIdx = screens.findIndex(s => s.id === targetId);
    const reordered = [...screens];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    const updated = reordered.map((s, i) => ({ ...s, position: i }));
    onScreensChange(updated);
    setDraggingId(null);
    setDragOverId(null);
    try {
      await Promise.all(updated.map(s => screensApi.update(event.id, s.id, { position: s.position })));
    } catch {
      toast.error('Error al reordenar');
      onScreensChange(screens);
    }
  };

  useEffect(() => {
    if (event) {
      setEventForm({
        name: event.name || '',
        description: event.description || '',
        scheduled_date: toDateTimeLocal(event.scheduled_date),
        streaming_url: event.streaming_url || '',
      });
    }
  }, [event, open]);

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setSavingEvent(true);
    try {
      const res = await eventsApi.update(event.id, eventForm);
      onEventUpdate && onEventUpdate(res.data);
      toast.success('Ajustes del evento guardados');
    } catch {
      toast.error('Error al guardar los ajustes');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleAddResource = async () => {
    if (!newResourceName.trim()) return;
    setAddingResource(true);
    try {
      const position = screens.length;
      const res = await screensApi.create(event.id, { name: newResourceName.trim(), position });
      onScreensChange && onScreensChange([...screens, res.data]);
      setNewResourceName('');
      toast.success('Recurso añadido');
    } catch {
      toast.error('Error al añadir recurso');
    } finally {
      setAddingResource(false);
    }
  };

  const handleRenameResource = async (screen, newName) => {
    if (!newName.trim() || newName === screen.name) return;
    try {
      const res = await screensApi.update(event.id, screen.id, { name: newName.trim() });
      onScreensChange && onScreensChange(screens.map(s => s.id === screen.id ? res.data : s));
    } catch {
      toast.error('Error al renombrar recurso');
    }
  };

  const handleDeleteResource = async (screen) => {
    if (!window.confirm(`¿Eliminar el recurso "${screen.name}"? Se borrarán sus contenidos en todas las fases.`)) return;
    try {
      await screensApi.delete(event.id, screen.id);
      onScreensChange && onScreensChange(screens.filter(s => s.id !== screen.id));
      toast.success('Recurso eliminado');
    } catch {
      toast.error('Error al eliminar recurso');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Ajustes del evento
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger value="general" className="flex-1">General</TabsTrigger>
            <TabsTrigger value="resources" className="flex-1">
              <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
              Recursos
            </TabsTrigger>
          </TabsList>

          {/* ── GENERAL TAB ── */}
          <TabsContent value="general">
            <form onSubmit={handleSaveEvent} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="ev-name">Nombre del evento *</Label>
                <Input
                  id="ev-name"
                  value={eventForm.name}
                  onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ev-desc">Descripción</Label>
                <Textarea
                  id="ev-desc"
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ev-date">Fecha y hora programada</Label>
                <Input
                  id="ev-date"
                  type="datetime-local"
                  value={eventForm.scheduled_date}
                  onChange={(e) => setEventForm({ ...eventForm, scheduled_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ev-stream">URL de streaming</Label>
                <Input
                  id="ev-stream"
                  placeholder="https://..."
                  value={eventForm.streaming_url}
                  onChange={(e) => setEventForm({ ...eventForm, streaming_url: e.target.value })}
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={savingEvent}>
                  {savingEvent ? 'Guardando...' : 'Guardar ajustes'}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* ── RESOURCES TAB ── */}
          <TabsContent value="resources">
            <div className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Define los recursos de tu evento. Cada recurso aparece como una columna en la escaleta y puede contener imágenes, vídeos, documentos o audio por fase.
              </p>

              {/* Resource list */}
              <div className="space-y-2">
                {screens.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Sin recursos todavía.</p>
                )}
                {screens.map((screen) => (
                  <ResourceRow
                    key={screen.id}
                    screen={screen}
                    isDragging={draggingId === screen.id}
                    isDragOver={dragOverId === screen.id}
                    onRename={(name) => handleRenameResource(screen, name)}
                    onDelete={() => handleDeleteResource(screen)}
                    onDragStart={() => setDraggingId(screen.id)}
                    onDragOver={() => setDragOverId(screen.id)}
                    onDrop={() => handleDrop(screen.id)}
                    onDragEnd={() => { setDraggingId(null); setDragOverId(null); }}
                  />
                ))}
              </div>

              {/* Add resource */}
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Input
                  placeholder="Nombre del recurso (ej: Pantalla principal, Música de fondo…)"
                  value={newResourceName}
                  onChange={(e) => setNewResourceName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddResource(); } }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={addingResource || !newResourceName.trim()}
                  onClick={handleAddResource}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const ResourceRow = ({ screen, onRename, onDelete, isDragging, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(screen.name);

  const handleBlur = () => {
    setEditing(false);
    onRename(name);
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => { e.preventDefault(); onDragOver(); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      onDragEnd={onDragEnd}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md border bg-muted/20 transition-all
        ${isDragging ? 'opacity-40' : ''}
        ${isDragOver ? 'border-primary bg-primary/5' : 'border-border'}`}
    >
      <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0 cursor-grab active:cursor-grabbing" />
      {editing ? (
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => { if (e.key === 'Enter') handleBlur(); if (e.key === 'Escape') { setName(screen.name); setEditing(false); } }}
          className="h-7 text-sm flex-1"
        />
      ) : (
        <span
          className="flex-1 text-sm cursor-pointer hover:text-primary"
          onClick={() => setEditing(true)}
          title="Clic para renombrar"
        >
          {screen.name}
        </span>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

export default EventSettingsDialog;
